import { useEffect } from "react";
import { useTaskSelector } from "src/business-components/input/TaskSelector/TaskSelector.hooks";
import type { EmptyTaskOption, TasksItem } from "src/queries/tasks/tasks.types";

interface ComparativeAnalysisFormTaskProps {
  env?: string;
  pipelineName?: string;
  setIsValidValue: (isValid: boolean | undefined) => void;
  taskName?: string;
  disabled?: boolean;
  onChange?: (task: string | undefined) => void;
  pit?: string;
  isLoading?: boolean;
}

export const useComparativeAnalysisFormTask = ({
  env,
  pipelineName,
  setIsValidValue,
  taskName,
  disabled,
  onChange,
  pit,
  isLoading,
}: ComparativeAnalysisFormTaskProps) => {
  const {
    getTaskRunByName,
    task,
    tasks,
    isLoading: isLoadingTasks,
  } = useTaskSelector({
    env,
    app_pit: pit,
    task_name: taskName,
    app_name: pipelineName,
    disabled,
  });

  isLoading ||= isLoadingTasks;

  const onLocalChange = (task: TasksItem | EmptyTaskOption | undefined) => {
    // call "getTaskRunByName" to check if this task exists in the API
    const taskRun = getTaskRunByName(task?.task_name);

    onChange?.(
      taskRun ? taskRun.task_name + "-" + taskRun.task_id : task?.task_name + "-" + 0
    );
  };

  // make sure the `task` prop (derrived from URL query-param) exists in the tasks list
  useEffect(() => {
    // check if the current selected "task" (value) exists in the list of tasks from the API
    if (tasks.length && !disabled) {
      setIsValidValue(taskName ? !!task : undefined);
    }
  }, [setIsValidValue, tasks, taskName, task, disabled]);

  useEffect(() => {
    if (tasks.length) {
      onLocalChange(task);
    }
  }, [tasks, task, onLocalChange, pit]);

  return {
    task,
    options: tasks,
    isLoading,
    onChange: onLocalChange,
  };
};
